/**
 * Formatação das respostas de formulário para leitura — puro, sem React.
 *
 * FORM-37: a tabela de Respostas mostrava o código cru "PUBLICO" enquanto o
 * detalhe dizia "Link público", e campo DATA saía "2026-09-10" (ADR-0059:
 * data para o usuário é DD/MM/AAAA). O valor como foi gravado continua à
 * mão em `textoCruResposta`, para o `title`.
 *
 * Usado pela tela de Respostas (tabela e detalhe). É o formatador que a
 * exportação das respostas e o bloco de respostas do cockpit devem reusar.
 */
import {
  PONTO_DE_MILHAR,
  formatarDataLocal,
  formatarQuantidade,
} from "../../utils/dinheiro";
import {
  EFEITO_LEAD_LABEL,
  INTEGRACAO_FALHOU,
  MOTIVO_RECUSA_LABEL,
  STATUS_INTEGRACAO_LABEL,
  tipoSemInput,
} from "../../services/formularios";

const VAZIO = "—";

const ORIGEM_PUBLICO = "PUBLICO";

const ROTULO_ORIGEM = {
  PUBLICO: "Link público",
  INTERNO: "Painel interno",
};

// Campo DATA grava date-only ISO; outro formato (site externo) fica como veio.
const DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;

// Decimal como o JSON/Number grava: "12.5", "-3", "1000".
const DECIMAL_CANONICO = /^-?\d+(\.\d+)?$/;

/** Código da origem da submissão -> rótulo para o usuário. */
export function rotuloOrigem(origem) {
  return ROTULO_ORIGEM[origem] || origem || VAZIO;
}

/** Cor só comunica estado: público = info, interno = neutral. */
export function tomOrigem(origem) {
  return origem === ORIGEM_PUBLICO ? "info" : "neutral";
}

/** Data/hora de recebimento; inválida vira travessão, nunca "Invalid Date". */
export function formatarRecebidaEm(iso) {
  if (!iso) return VAZIO;
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? VAZIO : data.toLocaleString("pt-BR");
}

function formatarData(valor) {
  const texto = String(valor);
  return DATA_ISO.test(texto) ? formatarDataLocal(texto) : texto;
}

/**
 * Vírgula decimal e sem milhar (quantidade, V22). Texto ambíguo fica como
 * veio: "80.000" pode ser oitenta mil e Number() leria 80 — mostrar 80 seria
 * o "calcula certo e mostra errado" do ADR-0059.
 */
function formatarNumero(valor) {
  // Number ja e' um numero: 2.375 (m²) e' dois virgula tres, nao dois mil.
  // So o TEXTO e' ambiguo — "80.000" pode ser oitenta mil.
  if (typeof valor === "number") {
    return Number.isFinite(valor) ? formatarQuantidade(valor) : String(valor);
  }
  const texto = String(valor).trim();
  if (!DECIMAL_CANONICO.test(texto) || PONTO_DE_MILHAR.test(texto)) {
    return String(valor);
  }
  return formatarQuantidade(Number(texto));
}

const FORMATADOR_POR_TIPO = {
  DATA: formatarData,
  NUMERO: formatarNumero,
};

/**
 * Valor de uma resposta para exibição, conforme o tipo do campo. Lista vira
 * texto separado por vírgula; booleano vira Sim/Não.
 */
// Campo ARQUIVO (FORM-05): a resposta guarda `[{nome, anexo_id}]`.
function nomesDeArquivo(lista) {
  return lista.map((v) => (v && typeof v === "object" ? v.nome || "arquivo" : String(v)));
}

export function formatarValorResposta(campo, valor) {
  if (valor === null || valor === undefined || valor === "") return VAZIO;
  if (Array.isArray(valor)) return valor.length ? nomesDeArquivo(valor).join(", ") : VAZIO;
  if (typeof valor === "boolean") return valor ? "Sim" : "Não";
  const formatar = FORMATADOR_POR_TIPO[campo?.tipo];
  return formatar ? formatar(valor) : String(valor);
}

/** Valor como foi gravado, em texto — para o `title` ao lado do formatado. */
export function textoCruResposta(valor) {
  if (valor === null || valor === undefined) return "";
  if (Array.isArray(valor)) return nomesDeArquivo(valor).join(", ");
  return String(valor);
}

/** `status_integracao` -> rotulo (FORM-13). */
export function rotuloStatusIntegracao(status) {
  return STATUS_INTEGRACAO_LABEL[status] || status || VAZIO;
}

/** Cor so comunica estado: FALHOU = danger, o resto = success. */
export function tomStatusIntegracao(status) {
  return status === INTEGRACAO_FALHOU ? "danger" : "success";
}

/** `efeito_lead` -> rotulo (FORM-24). */
export function rotuloEfeitoLead(efeito) {
  return EFEITO_LEAD_LABEL[efeito] || efeito || VAZIO;
}

/** Motivo de recusa contado -> rotulo (FORM-13). */
export function rotuloMotivoRecusa(motivo) {
  return MOTIVO_RECUSA_LABEL[motivo] || motivo || VAZIO;
}

/**
 * Chaves de `dados` que nao sao campo atual do formulario, com o rotulo do
 * `campos_snapshot` gravado no envio (FORM-17). Campo excluido do formulario
 * continua legivel; sem snapshot (resposta antiga), vale "Campo removido".
 *
 * Devolve [{campo_id, label, tipo, valor}] na ordem das chaves de `dados`.
 */
export function camposRemovidosDaResposta(resposta, camposAtuais = []) {
  const dados = resposta?.dados && typeof resposta.dados === "object" ? resposta.dados : {};
  const snapshot =
    resposta?.campos_snapshot && typeof resposta.campos_snapshot === "object"
      ? resposta.campos_snapshot
      : {};
  const atuais = new Set(camposAtuais.map((c) => String(c.id)));
  return Object.entries(dados)
    .filter(([chave]) => !atuais.has(String(chave)))
    .map(([chave, valor]) => {
      const meta = snapshot[chave] && typeof snapshot[chave] === "object" ? snapshot[chave] : {};
      return {
        campo_id: String(chave),
        label: meta.label || "Campo removido",
        tipo: meta.tipo || "",
        valor,
      };
    });
}

/**
 * Colunas da exportacao/tabela: campos atuais com input mais os removidos
 * (FORM-17), sem repetir id. `camposRemovidos` vem de `metricas.campos_removidos`.
 */
export function colunasDeCampos(camposAtuais = [], camposRemovidos = []) {
  const vistos = new Set();
  const colunas = [];
  camposAtuais
    .filter((c) => !tipoSemInput(c.tipo))
    .forEach((c) => {
      const id = String(c.id);
      if (vistos.has(id)) return;
      vistos.add(id);
      colunas.push({ id, label: c.label || c.tipo, campo: c, removido: false });
    });
  camposRemovidos.forEach((c) => {
    const id = String(c.campo_id);
    if (vistos.has(id)) return;
    vistos.add(id);
    colunas.push({
      id,
      label: c.label || "Campo removido",
      campo: { id, tipo: c.tipo || "", label: c.label },
      removido: true,
    });
  });
  return colunas;
}
